import { Table, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { CURRENCY } from "Constants";
import { createComponentStore } from "easy-peasy";
import { ParameterStoreModel } from "store/models/parameters/general-parameter";
import { formatCurrency, IsFloat } from "Utils";

var _groupBy = require("lodash/groupBy");

const useParameterStore = createComponentStore(
  ParameterStoreModel("conditiontype")
);

export default function ContractInterests({
  payments = [],
  cashFlow = [],
  ...props
}) {
  const [interests, setInterests] = useState([]);
  const [store, actions] = useParameterStore();

  useEffect(() => {
    actions.fetch();
  }, [actions.fetch]);

  const columns = [
    {
      title: "Borç Dönemi",
      dataIndex: "dateOfDebt",
      key: "dateOfDebt",
      render: (text, record) => moment(record.dateOfDebt).format("DD.MM.YYYY"),
    },
    {
      title: "Vade Tarihi",
      dataIndex: "dateOfMaturity",
      key: "dateOfMaturity",
      render: (text, record) =>
        moment(record.dateOfMaturity).format("DD.MM.YYYY"),
    },
    {
      title: "Borç Tutarı",
      dataIndex: "debtAmount",
      key: "debtAmount",
      render: (text, record) => {
        return formatCurrency(record.debtAmount, record.debtCurrency);
      },
    },
    {
      title: "Faiz Tutarı",
      dataIndex: "interestAmount",
      key: "interestAmount",
      render: (text, record) => {
        return formatCurrency(record.interestAmount, record.debtCurrency);
      },
    },
    {
      title: "Toplam Tutarı",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (text, record) => {
        return formatCurrency(
          record.debtAmount + record.interestAmount,
          record.debtCurrency
        );
      },
    },
    {
      title: "Tahsilat Tarihi",
      dataIndex: "dateOfPayment",
      key: "dateOfPayment",
      render: (text, record) => {
        if (record.dateOfPayment)
          return moment(record.dateOfPayment).format("DD.MM.YYYY");
      },
    },
    {
      title: "Tahsilat Tutarı",
      dataIndex: "paymentAmount",
      key: "paymentAmount",
      render: (text, record) => {
        return formatCurrency(
          record.paymentAmount || 0,
          record.paymentCurrency
        );
      },
    },
    {
      title: "Kalan Borç Tutarı",
      dataIndex: "remainingDebtAmount",
      key: "remainingDebtAmount",
      render: (text, record) => {
        return formatCurrency(
          record.remainingDebtAmount || 0,
          record.debtCurrency
        );
      },
    },
    {
      title: "Kalan Alacak Tutarı",
      dataIndex: "remainingCreditAmount",
      key: "remainingCreditAmount",
      render: (text, record) => {
        return formatCurrency(
          record.remainingCreditAmount || 0,
          record.debtCurrency
        );
      },
    },
    {
      title: "Gün Sayısı",
      dataIndex: "interestDay",
      key: "interestDay",
    },
  ];

  const getInterestRate = (conditionTypeId, dateOfMaturity) => {
    const conditionType = store.data.find((f) => f.id === conditionTypeId);
    const interest = conditionType.interests.find((f) =>
      moment(dateOfMaturity).isBetween(f.dateOfBeginning, f.dateOfFinish)
    );

    return interest?.percentage / 100 || 0;
  };

  useEffect(() => {
    if (store.data.length <= 0) return;
    if (cashFlow.length > 0) {
      let processedCashFlow = cashFlow.map((item) => {
        if (item.type.parentInterestConditionType)
          item.type = item.type.parentInterestConditionType;

        return item;
      });

      var groupedCashFlow = _groupBy(
        processedCashFlow,
        function (cashflowItem) {
          return `${cashflowItem.type?.description}`;
        }
      );

      let interests = [];
      for (const [key, value] of Object.entries(groupedCashFlow)) {
        let groupedInterests = [];

        let summed = [];
        value.reduce(function (prev, curr) {
          let period =
            moment(curr.dateOfMaturity).month() +
            " " +
            moment(curr.dateOfMaturity).year();

          if (!prev[period]) {
            prev[period] = { ...curr, documentAmount: 0 };
            summed.push(prev[period]);
          }
          prev[period].documentAmount += curr.documentAmount;

          if (IsFloat(prev[period].documentAmount))
            prev[period].documentAmount = parseFloat(
              prev[period].documentAmount.toFixed(2)
            );

          return prev;
        }, {});

        let debts = summed; /*.filter((f) =>
          moment(f.dateOfMaturity).startOf("day").isBefore(moment())
        );*/

        let _payments =
          payments.length > 0
            ? payments.filter((f) => f.conditionType.description === key)
            : [];

        let paymentIndex = 0;
        let cashflowIndex = 0;
        let index = 0;
        let previousRow = {};

        while (cashflowIndex < debts.length) {
          let row = {};

          row.id = index;
          row.dateOfDebt = moment(debts[cashflowIndex].dateOfMaturity);
          row.dateOfMaturity =
            previousRow.remainingDebtAmount > 0 &&
            previousRow.dateOfPayment?.isAfter(previousRow.dateOfMaturity)
              ? moment(previousRow.dateOfPayment)
              : moment(debts[cashflowIndex].dateOfMaturity);
          row.debtAmount =
            row.dateOfDebt.isSame(previousRow.dateOfDebt) &&
            previousRow.remainingDebtAmount > 0
              ? previousRow.remainingDebtAmount
              : debts[cashflowIndex].documentAmount;
          row.debtCurrency = debts[cashflowIndex].documentCurrency;
          if (_payments[paymentIndex]?.paymentDate)
            row.dateOfPayment = moment(
              _payments[paymentIndex]?.paymentDate
            ).startOf("day");
          row.paymentAmount =
            previousRow.remainingCreditAmount > 0
              ? previousRow.remainingCreditAmount
              : _payments[paymentIndex]?.amount || 0;
          row.paymentCurrency = _payments[paymentIndex]?.currency || "";

          row.interestDay = 0;

          if (
            row.debtAmount > 0 &&
            row.dateOfMaturity.isBefore(row.dateOfPayment || moment())
          ) {
            row.interestDay = (
              row.dateOfPayment || moment().startOf("day")
            ).diff(row.dateOfMaturity.startOf("day"), "d");
          }

          row.interestAmount =
            row.debtAmount > 0
              ? row.debtAmount *
                row.interestDay *
                (getInterestRate(
                  debts[cashflowIndex].type.id,
                  row.dateOfMaturity
                ) /
                  365)
              : 0;
          row.totalAmount = row.debtAmount + row.interestAmount;

          row.remainingDebtAmount = 0;
          row.remainingCreditAmount = 0;

          let remaining = row.totalAmount - row.paymentAmount;

          if (row.paymentAmount > 0 && row.interestAmount > row.paymentAmount) {
            row.lessThenInsterest = true;
            paymentIndex++;
          } else {
            if (previousRow.lessThenInsterest)
              remaining = remaining - previousRow.paymentAmount;

            if (remaining > 0) row.remainingDebtAmount = remaining;
            else if (remaining < 0) row.remainingCreditAmount = remaining * -1;

            if (row.paymentAmount === 0) cashflowIndex++;
            else {
              if (row.remainingDebtAmount > 0) paymentIndex++;
              if (row.remainingCreditAmount > 0) cashflowIndex++;
              if (
                row.remainingCreditAmount === 0 &&
                row.remainingDebtAmount === 0
              ) {
                paymentIndex++;
                cashflowIndex++;
              }
            }
          }
          groupedInterests.push(row);
          previousRow = row;

          index++;
        }

        interests.push({ title: key, children: groupedInterests });
      }
      setInterests(interests);
    }
  }, [payments, cashFlow, store.data]);

  return (
    <Tabs>
      {interests.map((m, index) => {
        return (
          <Tabs.TabPane tab={m.title} key={index}>
            <Table
              size="small"
              scroll={{ x: 1000 }}
              bordered
              rowKey={(row) => row.id}
              dataSource={m.children}
              columns={columns}
              pagination={false}
            />
          </Tabs.TabPane>
        );
      })}
    </Tabs>
  );
}
